/**
 * ============================================================================
 * THE PRICING LADDER — SINGLE SOURCE OF TRUTH
 * ============================================================================
 *
 * Every tier number the app enforces or displays lives here: display name,
 * prices, monthly session cap, deep-work allowance, PDF access, and which tier
 * is the natural upgrade. Enforcement (`limits.ts`, `deepWork.ts`), Stripe
 * price provisioning (`stripe.ts`), over-limit copy, and the frontend pricing
 * surfaces all derive from this table.
 *
 * WHY THIS FILE EXISTS: these numbers used to be duplicated across
 * `limits.ts`, `stripe.ts`, the landing page, the settings page and the
 * over-cap dialog. When the Pro cap changed from 35 to 30, it had to be edited
 * in six places and the copy drifted — some surfaces still said 35. Change a
 * number here and every surface follows. Do not hardcode a tier price, cap, or
 * allowance anywhere else.
 *
 * ---------------------------------------------------------------------------
 * WHAT EACH TIER IS *FOR* (this is the product story, not just quota)
 * ---------------------------------------------------------------------------
 *   Starter  — test one idea.
 *   Standard — validate an idea properly: full strategy runs with all three
 *              specialists.
 *   Plus     — take it from idea to concept and prototype. This is the tier
 *              that unlocks the three deep-work generators (AI design
 *              breakdown with material options, customer discovery interview
 *              kit, business model canvas).
 *   Pro      — run a whole portfolio.
 *
 * The tiers are deliberately differentiated by CAPABILITY, not only volume, so
 * a visitor can tell which one they are. Plus is the "I've stopped wondering
 * whether this is a good idea and started figuring out how to build it" tier.
 *
 * ---------------------------------------------------------------------------
 * MARGIN BASIS — re-check this before changing any number
 * ---------------------------------------------------------------------------
 * Measured real AI cost: ~$0.65 per strategy session + memo, ~$0.10 per
 * deep-work generation. Every paid tier is set to hold gross margin above a
 * ~75% floor even if a subscriber maxes every cap in the same month:
 *
 *   Standard  ~$2.04/mo cost  -> 84.3% at $12.99  (89.8% month-to-month)
 *   Plus      ~$9.36/mo cost  -> 81.3% at $49.99  (87.5% month-to-month)
 *   Pro      ~$23.40/mo cost  -> 80.5% at $119.99 (87.0% month-to-month)
 *
 * These are MODELED at full utilisation, and they are exposed as
 * `estimatedMonthlyCostCents` on each tier so the unit-economics page can
 * import the basis rather than retyping it. Real measured cost now accrues in
 * `ai_usage_events`; when enough has, replace these estimates with the
 * observed figures and update the percentages above.
 *
 * Two traps worth remembering:
 *  - Standard cannot go to 4 sessions. It looks affordable now that deep-work
 *    moved off it, but 4 x $0.65 against $9.99 lands at ~73%, under the floor.
 *  - Pro cannot go back to 35 sessions for the same reason (~73%).
 *
 * The paid tiers also sit on a deliberately linear per-session line —
 * $3.33/session billed annually, $5.00/session month-to-month — so the ladder
 * reads as honest capacity pricing rather than arbitrary steps.
 */

/**
 * All tiers, including free. `inventor` and `startup` are legacy ids kept for
 * backward compatibility with existing `user.subscriptionPlan` values; their
 * display names ("Standard", "Pro") are what users actually see.
 */
export type UserTier = 'free' | 'inventor' | 'plus' | 'startup';

/** The paid tiers — the ones that have Stripe prices. */
export type PlanId = 'inventor' | 'plus' | 'startup';

export type BillingPeriod = 'annual' | 'monthly';

export interface PricingTier {
  id: UserTier;
  /** User-facing name. Never show the raw id. */
  displayName: string;
  /**
   * Headline price in cents: the effective MONTHLY cost when billed annually.
   * This is the number the pricing page leads with. `null` for free.
   */
  monthlyEquivalentCents: number | null;
  /** What Stripe actually charges once a year, in cents. `null` for free. */
  annualTotalCents: number | null;
  /** Month-to-month price in cents. `null` for free. */
  monthToMonthCents: number | null;
  /**
   * Modeled AI cost to serve one subscriber for a month at FULL cap use, in
   * cents. The basis behind the margin floor documented at the top of this file.
   *
   * Exposed as data so the unit-economics page imports it rather than retyping
   * the figures out of a comment. It is an estimate, not a measurement — real
   * per-user cost now accrues in `ai_usage_events` and the page shows the
   * observed figure alongside this one so the gap is visible.
   */
  estimatedMonthlyCostCents: number;
  /** Hard cap on strategy sessions + memos per calendar month. */
  sessionsPerMonth: number;
  /**
   * Distinct ideas that may receive deep-work AI generation per month
   * (design breakdown / customer kit / business canvas). 0 = not included in
   * this tier at all.
   */
  deepWorkIdeasPerMonth: number;
  /**
   * Spark runs allowed per calendar month for a signed-in member (one run =
   * one vent → directions → commercial-potential report). Resets monthly.
   *
   * Spark is the free ideation front door; for signed-in users it becomes an
   * always-available in-app tool that funnels into paid strategy sessions. The
   * monthly allowance is deliberately a modest taste — enough that nobody hits
   * a wall at the ideation moment, small enough that Spark reads as earned, not
   * free. Members who want more buy the non-expiring Spark overflow pack
   * (`SPARK_RUNS_PER_PACK` for `SPARK_PACK_PRICE_CENTS`), drawn down only after
   * this monthly allowance is spent:
   *
   *   Starter   2/mo
   *   Standard  5/mo
   *   Plus     10/mo
   *   Pro      20/mo
   *
   * Anonymous /spark visitors are metered separately (3 total, lifetime) by
   * `SPARK_ANON_CAP` in `common/sparkCap.ts`.
   */
  sparkRunsPerMonth: number;
  /**
   * Design Sketch runs included each month (each run draws three options).
   * Resets on the 1st (UTC), no rollover. Free: none until purchases exist.
   */
  designSketchesPerMonth: number;
  /** Whether the confidential memo PDF can be downloaded. */
  pdfDownload: boolean;
  /** The tier a user on this one should upgrade to. `null` = top of ladder. */
  upgradeTo: PlanId | null;
  /** One-line positioning, used on pricing surfaces and upgrade prompts. */
  tagline: string;
}

/**
 * The gross-margin floor every paid tier is designed to hold, even when a
 * subscriber maxes every cap in the same month.
 *
 * Exported so the unit-economics page imports the threshold rather than
 * retyping it — if the margin basis moves, the alarm moves with it.
 */
export const MARGIN_FLOOR_PCT = 75;

export const PRICING_TIERS: Record<UserTier, PricingTier> = {
  free: {
    id: 'free',
    // One free session a month at the measured ~$0.65 per session.
    estimatedMonthlyCostCents: 65,
    displayName: 'Starter',
    monthlyEquivalentCents: null,
    annualTotalCents: null,
    monthToMonthCents: null,
    sessionsPerMonth: 1,
    deepWorkIdeasPerMonth: 0,
    sparkRunsPerMonth: 2,
    designSketchesPerMonth: 0,
    pdfDownload: false,
    upgradeTo: 'inventor',
    tagline: 'Test one idea, free.',
  },
  inventor: {
    id: 'inventor',
    estimatedMonthlyCostCents: 204,
    displayName: 'Standard',
    monthlyEquivalentCents: 1299,
    annualTotalCents: 15588,
    monthToMonthCents: 1999,
    sessionsPerMonth: 3,
    // Standard deliberately does NOT include deep-work. Those three
    // generators are what Plus is for; see the tier story above.
    deepWorkIdeasPerMonth: 0,
    sparkRunsPerMonth: 5,
    designSketchesPerMonth: 1,
    pdfDownload: true,
    upgradeTo: 'plus',
    tagline: 'Validate an idea properly, with all three specialists.',
  },
  plus: {
    id: 'plus',
    estimatedMonthlyCostCents: 936,
    displayName: 'Plus',
    monthlyEquivalentCents: 4999,
    annualTotalCents: 59988,
    monthToMonthCents: 7499,
    sessionsPerMonth: 12,
    deepWorkIdeasPerMonth: 4,
    sparkRunsPerMonth: 10,
    designSketchesPerMonth: 1,
    pdfDownload: true,
    upgradeTo: 'startup',
    tagline: 'Take it from idea to concept and prototype.',
  },
  // Id remains 'startup' for backward compatibility with existing
  // subscriptionPlan values in the database. Display is "Pro" everywhere.
  startup: {
    id: 'startup',
    estimatedMonthlyCostCents: 2340,
    displayName: 'Pro',
    monthlyEquivalentCents: 11999,
    annualTotalCents: 143988,
    monthToMonthCents: 17999,
    sessionsPerMonth: 30,
    deepWorkIdeasPerMonth: 10,
    sparkRunsPerMonth: 20,
    designSketchesPerMonth: 2,
    pdfDownload: true,
    upgradeTo: null,
    tagline: 'Run a whole portfolio of ideas.',
  },
};

/** Ladder order, lowest to highest. Use for any ordered rendering. */
export const TIER_ORDER: UserTier[] = ['free', 'inventor', 'plus', 'startup'];

/** The paid tiers in ladder order. */
export const PAID_PLAN_IDS: PlanId[] = ['inventor', 'plus', 'startup'];

// ---------------------------------------------------------------------------
// The founding rate
// ---------------------------------------------------------------------------

/**
 * The founding-member price set: what early subscribers pay.
 *
 * `PRICING_TIERS` above holds the REGULAR rate — the canonical answer to "what
 * does this cost". This holds the lower rate offered while the founding window
 * is open, and it is what every new subscriber is actually charged while
 * `active` is true.
 *
 * ---------------------------------------------------------------------------
 * WHAT MAY AND MAY NOT BE CLAIMED
 * ---------------------------------------------------------------------------
 * `closesAt` is deliberately null: there is no committed end date yet.
 *
 * While it is null, NO SURFACE MAY STATE A FUTURE REGULAR PRICE. Printing
 * "regular rate $12.99 from [date]" without a date to stand behind is a
 * fictitious reference price aimed forward instead of backward, and it carries
 * the same exposure under EU Omnibus and UK CMA rules as a fabricated "was"
 * price. It also undercuts a product whose credibility rests on telling
 * inventors uncomfortable truths.
 *
 * So the founding rate is presented as what it is — a founding rate, locked for
 * as long as the subscription stays active — with no claim about what replaces
 * it. Set a real `closesAt` and the comparison copy can switch on; the regular
 * figures are already in place for it.
 *
 * CLOSING THE WINDOW is a one-line change: `active: false`. Every surface
 * follows, because they all derive from this table. Existing subscribers are
 * unaffected — a Stripe subscription stays on the price object it was created
 * with, so grandfathering is a property of how Stripe bills rather than
 * something this code maintains.
 */
export const FOUNDING_RATE = {
  /**
   * OFF. The site displays and charges the regular prices above.
   *
   * The machinery is kept rather than deleted because it is dormant and costs
   * nothing: flipping this to `true` (and setting a real `closesAt`) is all a
   * future founding-member or launch-promo window needs. Deleting it would mean
   * rebuilding the checkout, plan-change and grandfathering paths from scratch.
   *
   * Note what stays true with it off: the ONE existing subscriber remains on the
   * price object their subscription was created against, so they keep their
   * original rate automatically. Stripe never re-prices a live subscription.
   */
  active: false,
  /** No committed close date yet. See the note above before adding one. */
  closesAt: null as Date | null,
  /**
   * The founding prices. These are the amounts the app charged before the
   * regular rate was raised, which is why the existing Stripe price objects can
   * serve as the founding price objects with no migration.
   */
  prices: {
    inventor: { monthlyEquivalentCents: 999, annualTotalCents: 11988, monthToMonthCents: 1499 },
    plus: { monthlyEquivalentCents: 3999, annualTotalCents: 47988, monthToMonthCents: 5999 },
    startup: { monthlyEquivalentCents: 9999, annualTotalCents: 119988, monthToMonthCents: 14999 },
  },
} as const;

/** Whether the founding window is currently open. */
export function foundingRateActive(now: number = Date.now()): boolean {
  if (!FOUNDING_RATE.active) return false;
  const closes = FOUNDING_RATE.closesAt;
  return closes === null || now < closes.getTime();
}

/**
 * The price a given tier/period costs, honouring the founding window.
 *
 * `founding` should be true when the caller is a founding member (their lock
 * carries across plans) or when the window is open for a new subscriber.
 */
export function priceCentsFor(
  plan: PlanId,
  period: BillingPeriod,
  opts: { founding?: boolean } = {},
): number {
  const table = opts.founding ? FOUNDING_RATE.prices[plan] : PRICING_TIERS[plan];
  return (period === 'annual' ? table.annualTotalCents : table.monthToMonthCents) ?? 0;
}

/** The headline monthly-equivalent figure, honouring the founding window. */
export function monthlyEquivalentFor(plan: PlanId, opts: { founding?: boolean } = {}): number {
  const table = opts.founding ? FOUNDING_RATE.prices[plan] : PRICING_TIERS[plan];
  return table.monthlyEquivalentCents ?? 0;
}

/**
 * How far below the current regular rate an account's actual price sits, in
 * cents per year. Zero when they are on (or above) the regular rate.
 *
 * Deliberately computed from the price ACTUALLY charged rather than from
 * whether the founding window happens to be open. That makes it a general
 * "your rate is below current" signal: it keeps working after the founding
 * window closes, and it covers every future price rise automatically. Gating it
 * on the promo flag would strip every locked subscriber of their retention
 * asset the day the promo ended, which is the opposite of the point.
 */
export function lockedRateAdvantageCents(
  plan: PlanId,
  actualMonthlyCents: number | null | undefined,
  now: number = Date.now(),
): number {
  if (typeof actualMonthlyCents !== 'number' || actualMonthlyCents <= 0) return 0;
  // Compared against what a NEW subscriber would be charged TODAY, not against
  // the regular table price.
  //
  // This matters while the founding window is open: everyone pays the founding
  // rate then, so a founding member is not actually below anything and claiming
  // otherwise would be false. The advantage becomes real — and appears on its
  // own — the moment the window closes and new customers start paying more.
  const offeredNow = monthlyEquivalentFor(plan, { founding: foundingRateActive(now) });
  return Math.max(0, (offeredNow - actualMonthlyCents) * 12);
}

/**
 * Formatted headline prices for a tier, for use in marketing and UI copy.
 *
 * Exists so copy can interpolate a price instead of hardcoding one. Before this,
 * roughly forty hardcoded price strings were scattered across the landing page,
 * the how-it-works pages, the cap dialogs and the student page — meaning a price
 * change updated what Stripe charged while the site went on quoting the old
 * number. Reach for this rather than typing a figure.
 */
export function tierPriceLabels(plan: PlanId): {
  /** Effective monthly cost when billed annually, e.g. "$12.99". */
  annualMonthly: string;
  /** Month-to-month cost, e.g. "$19.99". */
  monthly: string;
  /** Total charged once a year, e.g. "$155.88". */
  annualTotal: string;
} {
  const t = PRICING_TIERS[plan];
  return {
    annualMonthly: formatPrice(t.monthlyEquivalentCents),
    monthly: formatPrice(t.monthToMonthCents),
    annualTotal: formatPrice(t.annualTotalCents),
  };
}

/** Position on the ladder, for "is this tier at least X" comparisons. */
export function tierRank(tier: UserTier): number {
  const i = TIER_ORDER.indexOf(tier);
  return i === -1 ? 0 : i;
}

/** True when `tier` sits at or above `minimum` on the ladder. */
export function tierAtLeast(tier: UserTier, minimum: UserTier): boolean {
  return tierRank(tier) >= tierRank(minimum);
}

/**
 * The lowest tier that includes deep-work generation. Derived rather than
 * hardcoded, so moving deep-work up or down the ladder is a one-line change
 * to the table above.
 */
export const DEEP_WORK_MIN_TIER: UserTier =
  TIER_ORDER.find((t) => PRICING_TIERS[t].deepWorkIdeasPerMonth > 0) ?? 'plus';

/** Format a cent amount as a plain dollar string ('$39.99', '$0'). */
export function formatPrice(cents: number | null): string {
  if (cents === null || cents === 0) return '$0';
  const dollars = cents / 100;
  // Whole dollars render without trailing zeros ('$499'), otherwise 2dp.
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}

/**
 * Coerce any incoming plan value to a valid paid plan id. Unknown and legacy
 * values ('monthly', 'annual') fall back to Standard, matching how
 * `getUserTier` resolves them.
 */
export function coercePlanId(raw: unknown): PlanId {
  return raw === 'startup' || raw === 'plus' ? raw : 'inventor';
}

/**
 * User-facing display name for any tier id. The ONLY way tier should ever be
 * rendered — never `tier.charAt(0).toUpperCase() + tier.slice(1)`, which prints
 * the raw legacy ids ("Inventor", "Startup") that users never see.
 *
 * An unrecognised value is returned verbatim rather than coerced to a plausible
 * default. A visibly odd label is a bug report; a wrong-but-plausible one is a
 * bug that ships and survives for months, which is exactly what happened when
 * the Plus tier was added and every stale three-tier lookup quietly relabelled
 * Plus subscribers as "Starter".
 */
export function tierDisplayName(raw: unknown): string {
  if (typeof raw === 'string' && raw in PRICING_TIERS) {
    return PRICING_TIERS[raw as UserTier].displayName;
  }
  return typeof raw === 'string' && raw ? raw : 'Unknown';
}

/**
 * The ACCOUNTING tier: what the books say this account is on.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT `getUserTier`. BOTH EXIST ON PURPOSE. DO NOT MERGE THEM.
 * ---------------------------------------------------------------------------
 *   `getUserTier(user)`   (common/limits.ts) — ACCESS truth. Routes through
 *      `hasPaidAccess`, so an unexpired support grace window keeps a past_due
 *      user on their paid tier. Session caps and PDF gating use this.
 *
 *   `getBillingTier(user)` (here)            — ACCOUNTING truth. Reads
 *      `subscriptionStatus === 'active'` directly and ignores grace entirely.
 *      Admin reporting, subscriber counts, and campaign segments use this.
 *
 * A support courtesy must unlock features without inflating subscriber counts
 * or revenue. Access honors grace; the books do not. So do NOT "fix" this to
 * call `hasPaidAccess` — that would silently book comped accounts as paying.
 *
 * This rule used to be copy-pasted as a local `tierOf` closure into seven
 * admin call sites. Every copy predated the Plus tier and ended with a bare
 * `return 'inventor'`, so Plus subscribers were reported as Standard
 * everywhere, and a campaign aimed at Standard also mailed every Plus
 * subscriber. It lives in exactly one place now.
 *
 * Takes a structural parameter rather than importing the `User` type on
 * purpose: the web interface imports this module at runtime for tier labels
 * and ladder order, and the web package does not depend on
 * `@mindstudio-ai/agent`. Importing a table module here — even as a type —
 * would put an unresolvable import in the frontend's type graph.
 */
export function getBillingTier(user: {
  subscriptionStatus?: string | null;
  subscriptionPlan?: string | null;
}): UserTier {
  if (user.subscriptionStatus !== 'active') return 'free';
  return coercePlanId(user.subscriptionPlan);
}

/**
 * The upgrade pitch shown when a user hits a limit: names the next tier up,
 * its annual-equivalent price, and what it actually buys. Returns null at the
 * top of the ladder (Pro), where the answer is to contact us instead.
 */
export function upgradePitch(tier: UserTier): {
  planId: PlanId;
  displayName: string;
  price: string;
  sessionsPerMonth: number;
  tagline: string;
  line: string;
} | null {
  const target = PRICING_TIERS[tier]?.upgradeTo;
  if (!target) return null;
  const next = PRICING_TIERS[target];
  const price = formatPrice(next.monthlyEquivalentCents);
  return {
    planId: target,
    displayName: next.displayName,
    price,
    sessionsPerMonth: next.sessionsPerMonth,
    tagline: next.tagline,
    line: `${next.displayName} at ${price}/mo gives you ${next.sessionsPerMonth} strategy sessions a month. ${next.tagline}`,
  };
}

/* ---------------------------------------------------------------------------
 * Overage pack
 * ------------------------------------------------------------------------- */

/*
 * These live in `pricing.ts`, not `limits.ts`, for one reason: the marketing
 * pages quote all three, and `limits.ts` imports the backend SDK so it cannot
 * be bundled for the browser. `limits.ts` re-exports them, so backend call
 * sites are unchanged.
 *
 * Quoting them from a shared constant rather than typing them into copy is not
 * hygiene for its own sake — the Students page carried a hardcoded "$14.99/mo"
 * Standard rate that kept advertising the pre-increase price after Stripe had
 * moved to $19.99.
 */

/**
 * Cents. The real charge runs through Stripe Checkout in
 * `createOverflowCheckoutSession`, and the capacity is granted by
 * `grantOverflowCredit` in the webhook on a genuinely paid session.
 *
 * `purchaseIdeaCredit` is a separate admin-only path that grants capacity
 * WITHOUT charging, so it deliberately never touches the paid revenue
 * counters — see the note in that file.
 */
export const EXTRA_IDEA_PRICE_CENTS = 1499;

/**
 * How many strategy sessions + memos one $14.99 overflow pack grants.
 *
 * This used to be 1, which made the overflow 4.5x worse than simply
 * subscribing ($9.99 buys three on Standard) and read as punitive. At 3 the
 * pack costs $5.00 per session, which is exactly the month-to-month rate — so
 * the ladder is coherent: buying on demand costs the same per unit as the
 * monthly plan, with no commitment.
 *
 * Margin: 3 x ~$0.65 measured AI cost = ~$1.95 against $14.99, ~87%.
 */
export const EXTRA_SESSIONS_PER_PACK = 3;

/**
 * How many build-tool ideas one build-tool pack grants (design breakdown,
 * customer discovery kit, business model canvas on that idea).
 *
 * Margin: 2 ideas x ~3 assets x ~$0.10 = ~$0.60 against $14.99, ~96%.
 */
export const EXTRA_DEEP_WORK_IDEAS_PER_PACK = 2;

/**
 * Price of one build-tool pack, in cents.
 *
 * Deliberately its own constant rather than reusing `EXTRA_IDEA_PRICE_CENTS`,
 * even though the two packs cost the same today. They are different goods with
 * very different margins (~87% for sessions, ~96% for build tools), so a future
 * change to one must not silently reprice the other. Each also has its own
 * immutable Stripe price object keyed to its own amount.
 */
export const EXTRA_DEEP_WORK_PRICE_CENTS = 1499;

/**
 * Spark overflow pack — extra in-app Spark ideation runs, bought once and
 * drawn down only AFTER a member's monthly tier allowance is spent.
 *
 * `SPARK_RUNS_PER_PACK` runs for `SPARK_PACK_PRICE_CENTS`. Unlike the session
 * and build-tool packs (month-scoped), Spark credits do NOT expire — they sit
 * as a durable balance in `spark_credits` and carry over until spent.
 *
 * Margin: 10 runs x ~$0.04 measured AI cost = ~$0.40 against $4.99, ~90% after
 * Stripe fees. A deliberately generous top-up whose real job is to keep an
 * engaged member ideating (and funneling into paid strategy sessions), not to
 * be a profit center. Its own constant + immutable Stripe price, same
 * discipline as the other one-offs.
 */
export const SPARK_PACK_PRICE_CENTS = 499;
export const SPARK_RUNS_PER_PACK = 10;

/**
 * Design Sketch: the 2D Sketch, bought per idea. One purchase = the drawings
 * for one idea (the three-option sketch for a physical product, the diagrams
 * for a method/service/software idea, both for a hybrid) with
 * `SKETCH_REDOS_PER_PURCHASE` redos. Measured AI cost is ~$0.30 per run, so a
 * purchase that uses both redos (~$0.93) is still ~84% gross.
 * Price history: $3.99 (plan) → $4.99 → $5.99 (2026-09-28, founder).
 */
export const SKETCH_PRICE_CENTS = 599;
export const SKETCH_REDOS_PER_PURCHASE = 2;

/** Component detail drawings included with every 2D Sketch (bought or plan-included), per idea. */
export const DETAILS_PER_SKETCH = 2;

/**
 * Component drawing packs: never-expiring detail drawings, usable on any idea,
 * spent after a sketch's included ones. One drawing is one generated image
 * (~$0.08–0.12 AI cost), so every size stays at 87–95% gross before fees.
 */
export const COMPONENT_PACKS = [
  { key: 'component_pack_5', slug: '5', units: 5, priceCents: 499, name: 'EntreDash 5 Part Close-ups' },
  { key: 'component_pack_15', slug: '15', units: 15, priceCents: 1299, name: 'EntreDash 15 Part Close-ups' },
  { key: 'component_pack_40', slug: '40', units: 40, priceCents: 2999, name: 'EntreDash 40 Part Close-ups' },
] as const;
export type ComponentPackKey = (typeof COMPONENT_PACKS)[number]['key'];
export const componentPackBySlug = (slug: string) => COMPONENT_PACKS.find((p) => p.slug === slug);

/**
 * Realistic render packs: never-expiring renders, usable on any model, spent
 * after a model version's included render and redo (`RENDERS_INCLUDED_PER_MODEL`).
 * Worst case per render is ~$0.25 of AI (render ~$0.11, the proportion check,
 * one corrective re-render), so every size stays at 85%+ gross before Stripe
 * fees even then (typical ~93%). Founder rule (2026-10-03): 85%+ before Stripe.
 */
export const RENDER_PACKS = [
  { key: 'render_pack_5', slug: '5', units: 5, priceCents: 899, name: 'EntreDash 5 Realistic Renders' },
  { key: 'render_pack_15', slug: '15', units: 15, priceCents: 2599, name: 'EntreDash 15 Realistic Renders' },
  { key: 'render_pack_40', slug: '40', units: 40, priceCents: 6799, name: 'EntreDash 40 Realistic Renders' },
] as const;
export type RenderPackKey = (typeof RENDER_PACKS)[number]['key'];
export const renderPackBySlug = (slug: string) => RENDER_PACKS.find((p) => p.slug === slug);
/** Renders included per model version with a Design Set: one render plus one redo. Failed renders never count. */
export const RENDERS_INCLUDED_PER_MODEL = 2;

/**
 * Design Set (sketch + 3D model + figure sheet), per idea, physical and hybrid
 * inventions only. ON SALE since 2026-09-29. Includes everything a 2D Sketch
 * does (3 options, 2 redos, 2 part close-ups) plus ONE 3D concept model build
 * on the method that suits the invention's shape (measured parts for boxy,
 * engineered designs; drawn shapes for round, smooth ones), a realistic render
 * plus one redo, and the figure sheet (free to remake, never metered).
 * Covers up to `MODEL_STANDARD_MAX_PARTS` main parts; bigger inventions add the
 * large-invention add-on. Rebuilds are bought separately (`MODEL_REBUILD_*`).
 * `DESIGN_SET_ON_SALE` stays as a kill switch.
 *
 * Price history: $14.99 (plan) → $17.99 (2026-09-29, founder, after the 3D
 * model's measured cost) → $39.99 (2026-10-03, founder). The $17.99 basis
 * ($0.72–1.97 per build) predated the outline-based build, which roughly
 * doubled cost; the routed builds measured ~$1.00–1.40 per main part
 * (pannier 4 parts $5.20–5.50, lantern 5 parts $6.96–8.04). At $39.99 a
 * 6-part routed build at full use (all sketch redos, both close-ups, both
 * renders) costs ~$9.70 of AI, ~76% gross before Stripe fees; a typical
 * 5-part order is 81–84%. No Design Set had been sold before the change.
 */
export const DESIGN_SET_ON_SALE = true;
export const DESIGN_SET_PRICE_CENTS = 3999;
export const DESIGN_SET_UPGRADE_CENTS = DESIGN_SET_PRICE_CENTS - SKETCH_PRICE_CENTS; // $34.00
/**
 * Design Set with your own model (2026-10-03, founder): everything in the
 * Design Set that works with an uploaded STL/OBJ/GLB (the 2D Sketch with its
 * redos and close-ups, viewer and downloads, a realistic render plus redo, the
 * figure sheet with model views, always), without the build-only features
 * (guided build, editor, rebuilds, large add-on). Worst-case AI ~$1.70 (sketch
 * + redos ~$0.93, close-ups ~$0.25, render + redo ~$0.50), ~87% gross before
 * Stripe. Upgrades are priced as the difference, so no path costs more.
 */
export const DESIGN_SET_UPLOAD_PRICE_CENTS = 1299;
/** 2D Sketch -> Design Set with your own model. */
export const DESIGN_SET_UPLOAD_UPGRADE_CENTS = DESIGN_SET_UPLOAD_PRICE_CENTS - SKETCH_PRICE_CENTS; // $7.00
/** Design Set with your own model -> the built Design Set. */
export const DESIGN_SET_BUILD_UPGRADE_CENTS = DESIGN_SET_PRICE_CENTS - DESIGN_SET_UPLOAD_PRICE_CENTS; // $27.00
/** Model upload limits: the file, the triangles read from it, and its real-world size once units apply. */
export const MODEL_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
export const MODEL_UPLOAD_MAX_TRIANGLES = 2_000_000;
/** Above this, the viewer gets a lighter copy (the original stays for download). */
export const MODEL_UPLOAD_VIEW_TRIANGLES = 300_000;
export const MODEL_UPLOAD_MIN_MM = 1;
export const MODEL_UPLOAD_MAX_MM = 10_000;
/** Builds included in a Design Set: the first build only. A failed build never counts. */
export const MODEL_BUILDS_PER_SET = 1;
/** Kept at 0: rebuilds are a separate purchase now (`MODEL_REBUILD_PRICE_CENTS`). */
export const MODEL_REBUILDS_PER_SET = 0;
/** Main parts a Design Set's build covers; more needs the large-invention add-on. */
export const MODEL_STANDARD_MAX_PARTS = 6;
/**
 * Large-invention add-on: 7–12 main parts (builds keep 12 main parts at most).
 * A 12-part routed build at full use costs ~$18 of AI; $39.99 + $34.99 keeps it
 * at ~76% gross.
 */
export const MODEL_LARGE_ADDON_CENTS = 3499;
/**
 * Rebuild with a note: rebuilds only the parts the note is about (up to
 * `REBUILD_MAX_PARTS`) and reuses every other part, its placement and the
 * earlier readings. Estimated ~$1–3.50 of AI, ~77%+ gross at $14.99.
 */
export const MODEL_REBUILD_PRICE_CENTS = 1499;
export const REBUILD_MAX_PARTS = 2;
/** Shortest rebuild note a member can send (a rebuild is never spent blind). */
export const REBUILD_NOTE_MIN = 8;
// Packs: about 8%, 10% and 12.5% off the single-set price. Never-expiring,
// spent one set at a time against any idea. All under the $499 affiliate floor
// (a 25-set pack at this price would cross it and pay the 40% commission, so it
// was retired).
export const DESIGN_SET_PACKS = [
  { key: 'design_set_pack_3', slug: '3', sets: 3, priceCents: 10999, name: 'EntreDash 3 Design Sets' },
  { key: 'design_set_pack_5', slug: '5', sets: 5, priceCents: 17999, name: 'EntreDash 5 Design Sets' },
  { key: 'design_set_pack_10', slug: '10', sets: 10, priceCents: 34999, name: 'EntreDash 10 Design Sets' },
] as const;
export type DesignSetPackKey = (typeof DESIGN_SET_PACKS)[number]['key'];
export const designSetPackBySlug = (slug: string) => DESIGN_SET_PACKS.find((p) => p.slug === slug);

/**
 * NOTE: there is no free vocabulary limit any more.
 *
 * A `FREE_VOCABULARY_LIMIT = 25` lived here during a brief window when free
 * accounts could save glossary terms. Saving is subscriber-only again, so a cap
 * has nothing to cap — and a leftover constant reads like live policy to whoever
 * finds it next. See `saveVocabularyTerm` for the current rule.
 */

/* ---------------------------------------------------------------------------
 * Single strategy-session ticket ($9.99)
 * ------------------------------------------------------------------------- */

/**
 * The $9.99 pay-as-you-go "Strategy session" ticket: ONE session + memo,
 * granted as a single non-expiring session credit.
 *
 * Distinct from the $14.99 Advisory-of-three pack (`EXTRA_SESSIONS_PER_PACK`)
 * on purpose — it is the lowest-commitment entry point ("just let me run one").
 * At $9.99 for one it deliberately sits ABOVE the $5.00/session unit rate of the
 * three-pack, so the pack is the better deal and the single is a genuine
 * on-ramp rather than a way to undercut the pack. Its own immutable Stripe
 * price object keyed to this amount.
 */
export const SINGLE_SESSION_PRICE_CENTS = 999;
export const SINGLE_SESSION_CREDITS = 1;

/* ---------------------------------------------------------------------------
 * Credit packs — bulk session credits + bundled perks
 * ------------------------------------------------------------------------- */

/**
 * How long a credit pack's BUNDLED PERKS stay usable after purchase.
 *
 * The session credits a pack grants NEVER expire (they fold into the same
 * durable overflow balance as every other purchased credit). Only the
 * sweeteners — the Spark run packs and the build-tool idea allowances — carry
 * this 12-month clock, tracked per-grant in `perk_grants` so each bundle
 * expires on its own schedule without a scheduled job.
 */
export const PERK_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

/** The three credit-pack SKUs. */
export type CreditPackKey = 'credit_pack_36' | 'credit_pack_144' | 'credit_pack_360';

export interface CreditPack {
  key: CreditPackKey;
  /** Short id used in URLs and button state ('36' | '144' | '360'). */
  slug: '36' | '144' | '360';
  /** Session credits granted (never expire). */
  credits: number;
  /** Charge amount, in cents. */
  priceCents: number;
  /** Bundled Spark run packs (each = SPARK_RUNS_PER_PACK runs). Perk, expires. */
  sparkPacks: number;
  /** Bundled build-tool idea allowances. Perk, expires. 0 = none in this pack. */
  deepWorkIdeas: number;
  /** Display name for receipts and the Stripe product. */
  name: string;
}

/**
 * The credit packs, single source of truth. Prices and perk counts are quoted
 * verbatim on the marketing pricing cards (`pricingStack.tsx` imports this), so
 * a change here follows every surface and can never drift from what Stripe
 * charges.
 *
 * Perk math: Spark runs = `sparkPacks x SPARK_RUNS_PER_PACK` (5/10/20 packs →
 * 50/100/200 runs). Build-tool ideas are total slots, drawn from a durable
 * 12-month perk pool.
 */
export const CREDIT_PACKS: CreditPack[] = [
  {
    key: 'credit_pack_36',
    slug: '36',
    credits: 36,
    priceCents: 17999,
    sparkPacks: 5,
    deepWorkIdeas: 0,
    name: 'EntreDash 36 Credits',
  },
  {
    key: 'credit_pack_144',
    slug: '144',
    credits: 144,
    priceCents: 69999,
    sparkPacks: 10,
    deepWorkIdeas: 4,
    name: 'EntreDash 144 Credits',
  },
  {
    key: 'credit_pack_360',
    slug: '360',
    credits: 360,
    priceCents: 159999,
    sparkPacks: 20,
    deepWorkIdeas: 10,
    name: 'EntreDash 360 Credits',
  },
];

/** Resolve a credit pack by its catalog key. */
export function creditPackByKey(key: string): CreditPack | undefined {
  return CREDIT_PACKS.find((p) => p.key === key);
}

/** Resolve a credit pack by its short slug ('36' | '144' | '360'). */
export function creditPackBySlug(slug: string): CreditPack | undefined {
  return CREDIT_PACKS.find((p) => p.slug === slug);
}

/* ---------------------------------------------------------------------------
 * Signals monitoring — free allowance + scan credit packs
 * -------------------------------------------------------------------------
 * Monitoring is metered in "scan units". A scan is NOT one unit flat — an
 * expensive signal (a live-search directional read) draws more units than a
 * cheap one (a news pass), so a single per-unit price holds a uniform margin.
 * The unit weights live in `common/signals/types.ts` (`SIGNAL_SCAN_WEIGHT`),
 * next to the scan engine; the money side lives here.
 *
 * Each paid plan includes a monthly free unit allowance (resets monthly, no
 * rollover). Beyond it, members buy scan credit packs — one-off, NEVER expire,
 * spent only after the month's free allowance is gone. Margins measured in dev
 * hold ~88–96% across the ladder (see the monitoring-economics reference). */

/** Free scan units included each month, by plan. Starter (free) gets none. */
export const FREE_SCAN_UNITS_BY_PLAN: Record<UserTier, number> = {
  free: 0,
  inventor: 20, // Standard
  plus: 40, // Plus
  startup: 80, // Pro
};

/** The five scan credit pack SKUs. */
export type ScanPackKey =
  | 'scan_pack_25'
  | 'scan_pack_50'
  | 'scan_pack_100'
  | 'scan_pack_500'
  | 'scan_pack_1000';

export interface ScanPack {
  key: ScanPackKey;
  /** Short id used in URLs and button state ('25' | '50' | ...). */
  slug: '25' | '50' | '100' | '500' | '1000';
  /** Scan units granted (never expire). */
  units: number;
  /** Charge amount, in cents. */
  priceCents: number;
  /** Display name for receipts and the Stripe product. */
  name: string;
}

/**
 * The scan packs, single source of truth. A gentle volume discount deepens with
 * size ($0.40/unit on the top-up down to $0.30/unit on the bulk pack); every
 * tier holds >=80% margin even if the whole pack is burned on the costliest
 * signal at worst-case cost.
 */
export const SCAN_PACKS: ScanPack[] = [
  { key: 'scan_pack_25', slug: '25', units: 25, priceCents: 999, name: 'EntreDash 25 Scans' },
  { key: 'scan_pack_50', slug: '50', units: 50, priceCents: 1899, name: 'EntreDash 50 Scans' },
  { key: 'scan_pack_100', slug: '100', units: 100, priceCents: 3499, name: 'EntreDash 100 Scans' },
  { key: 'scan_pack_500', slug: '500', units: 500, priceCents: 15999, name: 'EntreDash 500 Scans' },
  { key: 'scan_pack_1000', slug: '1000', units: 1000, priceCents: 29999, name: 'EntreDash 1,000 Scans' },
];

/** Resolve a scan pack by its catalog key. */
export function scanPackByKey(key: string): ScanPack | undefined {
  return SCAN_PACKS.find((p) => p.key === key);
}

/** Resolve a scan pack by its short slug ('25' | '50' | ...). */
export function scanPackBySlug(slug: string): ScanPack | undefined {
  return SCAN_PACKS.find((p) => p.slug === slug);
}

/** Excel download packs for the Live Models (2026-11-03). One credit makes one new workbook. */
export type ExcelPackKey = 'excel_pack_1' | 'excel_pack_5' | 'excel_pack_20' | 'excel_pack_50';

export interface ExcelPack {
  key: ExcelPackKey;
  slug: '1' | '5' | '20' | '50';
  /** Download credits granted (never expire). */
  credits: number;
  priceCents: number;
  name: string;
}

/**
 * The Excel download packs, single source of truth. Each workbook is made by our own code with no AI,
 * so every tier keeps a high margin. Credits never expire and work on any idea; any plan can buy them.
 */
export const EXCEL_PACKS: ExcelPack[] = [
  { key: 'excel_pack_1', slug: '1', credits: 1, priceCents: 499, name: 'EntreDash 1 Excel Download' },
  { key: 'excel_pack_5', slug: '5', credits: 5, priceCents: 1999, name: 'EntreDash 5 Excel Downloads' },
  { key: 'excel_pack_20', slug: '20', credits: 20, priceCents: 5999, name: 'EntreDash 20 Excel Downloads' },
  { key: 'excel_pack_50', slug: '50', credits: 50, priceCents: 11999, name: 'EntreDash 50 Excel Downloads' },
];

export function excelPackByKey(key: string): ExcelPack | undefined {
  return EXCEL_PACKS.find((p) => p.key === key);
}
export function excelPackBySlug(slug: string): ExcelPack | undefined {
  return EXCEL_PACKS.find((p) => p.slug === slug);
}

/** The Provisional Patent Prep Plan, per invention (search, Pathway Navigator, Prepare My Filing, manager included). See prepPlan.ts. */
export const PREP_PLAN_PRICE_CENTS = 144999;
/** The Cost & Filing Manager, per invention (included free with the Prep Plan). */
export const COST_MANAGER_PRICE_CENTS = 9999;
